import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  TableChart as ErpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InsertDriveFile as FileIcon,
  Storage as FirebaseIcon,
  Refresh as RefreshIcon,
  History as ReprocessIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  BrokenImage as BrokenImageIcon,
} from '@mui/icons-material';
import { listFirebaseDocuments, FirebaseDocument, fetchErpData, ErpDataSection, reprocessDocument, fetchDocumentPreviewUrl } from '../../services/api';
import ProcessingStatus, { ProcessingStep } from '../ProcessingStatus';
import { ComparisonResult, Classification } from '../../types';
import DocumentPreview from './DocumentPreview';

export type ProcessType = 'daily_fee' | 'unloading' | 'pallet' | 'vehicle';

export interface ReprocessResult {
  extracted_data?: any;
  comparison_result?: any;
  classification?: any;
  is_exception?: boolean;
  exception_reasons?: string[];
  message?: string;
}

interface FirebaseSelectorProps {
  processType: ProcessType;
  onDocumentSelect?: (docId: string, doc: FirebaseDocument | null) => void;
  onReprocessComplete?: (docId: string, result: ReprocessResult) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  afterProcessingContent?: React.ReactNode;
}

const PROCESS_LABELS: Record<ProcessType, string> = {
  daily_fee: 'Structured Data',
  unloading: 'Semi-Structured Data',
  pallet: 'Unstructured Data',
  vehicle: 'Image & Multimedia Data',
};

const reprocessSteps: ProcessingStep[] = [
  { label: 'Document Agent', description: 'Loading document from Firebase storage and preparing pipeline', status: 'pending' },
  { label: 'OCR Extraction Agent', description: 'Running OCR and extracting structured fields using Gemini Vision', status: 'pending' },
  { label: 'ERP Comparison Agent', description: 'Matching extracted fields against ERP records from data bucket', status: 'pending' },
  { label: 'Classification Agent', description: 'Analyzing results and classifying as standard or exception case', status: 'pending' },
];

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'processed': return 'success';
    case 'pending': return 'warning';
    case 'approved': return 'info';
    case 'rejected': return 'error';
    case 'requires_review': return 'warning';
    default: return 'default';
  }
};

const isImageFilename = (filename: string): boolean => {
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.avif'];
  return imageExts.some(ext => filename.toLowerCase().endsWith(ext));
};

const getFileTypeLabel = (filename: string): string => {
  const ext = filename.split('.').pop()?.toUpperCase() || 'FILE';
  return ext;
};

export default function FirebaseSelector({ processType, onDocumentSelect, onReprocessComplete, disabled, children, afterProcessingContent }: FirebaseSelectorProps) {
  // Firebase documents state
  const [firebaseDocuments, setFirebaseDocuments] = useState<FirebaseDocument[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [loadingDocs, setLoadingDocs] = useState(false);
  // Map of document_id -> preview URL for images
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  // Lightbox state: which doc is being previewed in fullscreen
  const [lightboxDoc, setLightboxDoc] = useState<FirebaseDocument | null>(null);

  // Reprocess state
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMessage, setReprocessMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Processing steps state
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>(reprocessSteps);
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isProcessingActive, setIsProcessingActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Comparison results state
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult[] | null>(null);
  const [classification, setClassification] = useState<Classification | null>(null);

  // ERP data state
  const [erpSections, setErpSections] = useState<ErpDataSection[]>([]);
  const [erpFilename, setErpFilename] = useState<string>('');
  const [loadingErp, setLoadingErp] = useState(false);
  const [erpVisible, setErpVisible] = useState(false);
  const [erpError, setErpError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  // Fetch filtered documents on mount
  useEffect(() => {
    fetchDocuments();
  }, [processType]);

  // Timer effect
  useEffect(() => {
    if (isProcessingActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isProcessingActive]);

  const updateStep = (stepIndex: number, status: ProcessingStep['status'], duration?: number) => {
    setProcessingSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status, duration } : step
    ));
    if (status === 'running') {
      setCurrentStep(stepIndex);
    }
  };

  const resetProcessingState = () => {
    setProcessingSteps(reprocessSteps.map(s => ({ ...s, status: 'pending' as const })));
    setCurrentStep(0);
    setElapsedTime(0);
    setIsProcessingActive(false);
  };

  const fetchDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const response = await listFirebaseDocuments(processType);
      const docs = response.documents || [];
      setFirebaseDocuments(docs);

      // For vehicle (image/multimedia) process type, fetch preview URLs for image files
      if (processType === 'vehicle') {
        const imgDocs = docs.filter(d => isImageFilename(d.filename));
        const urlMap: Record<string, string> = {};
        await Promise.allSettled(
          imgDocs.map(async (doc) => {
            try {
              const result = await fetchDocumentPreviewUrl(doc.document_id);
              if (result.preview_url) {
                urlMap[doc.document_id] = result.preview_url;
              }
            } catch {
              // silently skip if preview fetch fails
            }
          })
        );
        setPreviewUrls(urlMap);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  }, [processType]);

  const handleSelectDoc = (docId: string) => {
    setSelectedDocId(docId);
    // Clear previous results when changing selection
    if (!docId) {
      setComparisonResult(null);
      setClassification(null);
    }
    const selectedDoc = firebaseDocuments.find((d) => d.document_id === docId) || null;
    onDocumentSelect?.(docId, selectedDoc);
  };

  const handleReprocess = async () => {
    if (!selectedDocId) return;

    // Reset and start processing
    resetProcessingState();
    setIsProcessingActive(true);
    setReprocessing(true);
    setReprocessMessage(null);
    setComparisonResult(null);
    setClassification(null);

    // Step 1: Load Document
    const step1Start = Date.now();
    updateStep(0, 'running');

    try {
      console.log(`[FirebaseSelector] Reprocessing document: ${selectedDocId} (${processType})`);
      const response = await reprocessDocument(selectedDocId);

      const step1Duration = Math.round((Date.now() - step1Start) / 1000);
      updateStep(0, 'completed', step1Duration);

      // Step 2: OCR & Extraction
      updateStep(1, 'running');
      await new Promise(resolve => setTimeout(resolve, 600));
      updateStep(1, 'completed', 1);

      // Step 3: ERP Comparison
      updateStep(2, 'running');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStep(2, 'completed', 1);

      // Step 4: Classification
      updateStep(3, 'running');
      await new Promise(resolve => setTimeout(resolve, 400));
      updateStep(3, 'completed', 1);

      // Store comparison results for display
      if (response.comparison_result) {
        setComparisonResult(response.comparison_result);
      }
      if (response.classification) {
        setClassification(response.classification);
      }

      setReprocessMessage({
        type: 'success',
        text: '✅ Document reprocessed successfully with ERP comparison',
      });

      // Refresh document list
      fetchDocuments();

      // Notify parent component
      onReprocessComplete?.(selectedDocId, {
        extracted_data: response.extracted_data,
        comparison_result: response.comparison_result,
        classification: response.classification,
        is_exception: response.is_exception,
        exception_reasons: response.exception_reasons,
        message: response.message,
      });
    } catch (error: any) {
      console.error('[FirebaseSelector] Reprocess error:', error);
      updateStep(currentStep, 'error');
      setReprocessMessage({
        type: 'error',
        text: error.response?.data?.detail || error.message || 'Reprocessing failed',
      });
    } finally {
      setReprocessing(false);
      setIsProcessingActive(false);
    }
  };

  const handleViewErpData = async () => {
    if (erpVisible) {
      setErpVisible(false);
      return;
    }
    setLoadingErp(true);
    setErpError(null);
    try {
      const data = await fetchErpData(processType);
      setErpSections(data.sections || []);
      setErpFilename(data.filename || '');
      setErpVisible(true);
      // Auto-expand first section
      setExpandedSections(new Set([0]));
    } catch (err: any) {
      setErpError(err?.response?.data?.detail || err?.message || 'Failed to fetch ERP data');
    } finally {
      setLoadingErp(false);
    }
  };

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <>
      {/* ── Lightbox Modal ── */}
      {lightboxDoc && (
        <Box
          onClick={() => setLightboxDoc(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.88)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            backdropFilter: 'blur(6px)',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={() => setLightboxDoc(null)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: '#fff',
              backgroundColor: alpha('#fff', 0.12),
              '&:hover': { backgroundColor: alpha('#fff', 0.22) },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Image or no-storage fallback */}
          {previewUrls[lightboxDoc.document_id] ? (
            <Box
              component="img"
              src={previewUrls[lightboxDoc.document_id]}
              alt={lightboxDoc.filename}
              onClick={(e: any) => e.stopPropagation()}
              sx={{
                maxWidth: '88vw',
                maxHeight: '82vh',
                borderRadius: 3,
                boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
                objectFit: 'contain',
                cursor: 'default',
              }}
            />
          ) : (
            <Box
              onClick={(e: any) => e.stopPropagation()}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                color: '#fff',
                p: 4,
                borderRadius: 3,
                background: alpha('#1e1e2e', 0.8),
                border: '1px solid',
                borderColor: alpha('#fff', 0.1),
              }}
            >
              <BrokenImageIcon sx={{ fontSize: 64, color: alpha('#fff', 0.4) }} />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Preview unavailable
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), textAlign: 'center', maxWidth: 320 }}>
                This document has no file stored in cloud storage. It may have been created before the storage feature was added. Please re-upload it using the upload form below.
              </Typography>
              <Chip
                label={lightboxDoc.filename}
                size="small"
                sx={{ backgroundColor: alpha('#fff', 0.1), color: '#fff', fontWeight: 600 }}
              />
            </Box>
          )}

          {/* Caption */}
          {previewUrls[lightboxDoc.document_id] && (
            <Box
              onClick={(e: any) => e.stopPropagation()}
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                backgroundColor: alpha('#000', 0.5),
                borderRadius: 2,
                px: 2,
                py: 1,
                cursor: 'default',
              }}
            >
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                {lightboxDoc.filename}
              </Typography>
              <Chip
                label={lightboxDoc.status}
                size="small"
                color={getStatusColor(lightboxDoc.status) as any}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
              />
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                {formatDate(lightboxDoc.uploaded_at)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {/* Firebase Documents Dropdown */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: selectedDocId ? 'secondary.main' : 'divider',
          background: selectedDocId
            ? `linear-gradient(135deg, ${alpha('#3B82F6', 0.04)} 0%, ${alpha('#8B5CF6', 0.04)} 100%)`
            : 'background.paper',
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FirebaseIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Select from Firebase
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Choose a previously uploaded {PROCESS_LABELS[processType].toLowerCase()} document
            </Typography>
          </Box>
          <Tooltip title="Refresh document list">
            <IconButton
              size="small"
              onClick={fetchDocuments}
              disabled={loadingDocs}
              sx={{ color: 'text.secondary' }}
            >
              {loadingDocs ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel id={`firebase-doc-select-${processType}`}>
            {loadingDocs ? 'Loading documents...' : 'Select a document'}
          </InputLabel>
          <Select
            labelId={`firebase-doc-select-${processType}`}
            id={`firebase-doc-select-${processType}-input`}
            value={selectedDocId}
            label={loadingDocs ? 'Loading documents...' : 'Select a document'}
            onChange={(e) => handleSelectDoc(e.target.value as string)}
            disabled={loadingDocs || disabled}
            sx={{
              borderRadius: 2,
              '& .MuiSelect-select': { py: 1.5 },
            }}
          >
            <MenuItem value="">
              <em>— None (upload a new file) —</em>
            </MenuItem>
            {(processType === 'vehicle' ? firebaseDocuments.slice(0, 2) : firebaseDocuments).map((doc) => (
              <MenuItem key={doc.document_id} value={doc.document_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <FileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {doc.filename}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatDate(doc.uploaded_at)}
                      {doc.delivery_number && ` • DLV: ${doc.delivery_number}`}
                    </Typography>
                  </Box>
                  <Chip
                    label={doc.status}
                    size="small"
                    color={getStatusColor(doc.status) as any}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Reprocess button + Preview for existing document */}
        {selectedDocId && (
          <Fade in>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleReprocess}
                disabled={reprocessing || disabled}
                startIcon={reprocessing ? <CircularProgress size={18} color="inherit" /> : <ReprocessIcon />}
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {reprocessing ? 'Reprocessing...' : 'Reprocess with ERP Comparison'}
              </Button>
              <DocumentPreview
                firebaseDocId={selectedDocId}
                firebaseFilename={firebaseDocuments.find(d => d.document_id === selectedDocId)?.filename}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 200 }}>
                Runs ERP comparison & classification on the existing extracted data
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Reprocess Message */}
        {reprocessMessage && (
          <Alert severity={reprocessMessage.type} sx={{ mt: 2 }} onClose={() => setReprocessMessage(null)}>
            {reprocessMessage.text}
          </Alert>
        )}

      </Paper>

      {/* Children slot: Upload area, order inputs, etc. from parent */}
      {children}

      {/* Processing Status with Agent Steps */}
      <ProcessingStatus
        isProcessing={isProcessingActive}
        currentStep={currentStep}
        steps={processingSteps}
        elapsedTime={elapsedTime}
        estimatedTime={120}
      />

      {/* After-processing content slot: Extracted data tables from parent */}
      {afterProcessingContent}

      {/* Comparison with ERP Data Results */}
      {comparisonResult && comparisonResult.length > 0 && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              📊 Comparison with ERP Data
            </Typography>
            {(() => {
              const noDataCount = comparisonResult.filter(r => !r.document_value && !r.erp_value).length;
              // Only count fields that have ERP values for match rate
              const erpComparedResults = comparisonResult.filter(r => r.erp_value);
              const matchCount = erpComparedResults.filter(r => r.match).length;
              const totalErpCount = erpComparedResults.length;
              const matchPct = totalErpCount > 0 ? Math.round((matchCount / totalErpCount) * 100) : 0;
              const extractedOnlyCount = comparisonResult.filter(r => r.document_value && !r.erp_value).length;
              return (
                <>
                  <Chip
                    label={`${matchCount}/${totalErpCount} ERP fields match`}
                    size="small"
                    color={matchPct === 100 ? 'success' : matchPct >= 50 ? 'warning' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`${matchPct}% match rate`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444',
                      color: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444',
                      fontWeight: 600,
                    }}
                  />
                  {extractedOnlyCount > 0 && (
                    <Chip
                      label={`+${extractedOnlyCount} extracted fields`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#3B82F6',
                        color: '#3B82F6',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </>
              );
            })()}
          </Box>
          <TableContainer sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>📄 Document Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>🏢 ERP Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%', textAlign: 'center' }}>Match Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%' }}>Discrepancy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResult.map((result) => {
                  const bothEmpty = !result.document_value && !result.erp_value;
                  const isMatch = result.match && !bothEmpty;
                  const isNoData = bothEmpty;
                  const isExtractedOnly = !!result.document_value && !result.erp_value;

                  return (
                    <TableRow
                      key={result.field}
                      sx={{
                        backgroundColor: isNoData
                          ? alpha('#6B7280', 0.03)
                          : isExtractedOnly
                            ? alpha('#3B82F6', 0.03)
                            : isMatch
                              ? alpha('#10B981', 0.03)
                              : alpha('#EF4444', 0.05),
                        '&:hover': {
                          backgroundColor: isNoData
                            ? alpha('#6B7280', 0.08)
                            : isExtractedOnly
                              ? alpha('#3B82F6', 0.08)
                              : isMatch
                              ? alpha('#10B981', 0.08)
                              : alpha('#EF4444', 0.1),
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <strong>{result.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                      </TableCell>
                      <TableCell sx={{
                        color: result.document_value ? 'text.primary' : 'text.disabled',
                        fontFamily: result.document_value ? 'monospace' : 'inherit',
                      }}>
                        {result.document_value || '—  Not found'}
                      </TableCell>
                      <TableCell sx={{
                        color: result.erp_value ? 'text.primary' : 'text.disabled',
                        fontFamily: result.erp_value ? 'monospace' : 'inherit',
                      }}>
                        {result.erp_value || '—  Not available'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {isNoData ? (
                          <Chip
                            label="⚠ No Data"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#6B7280', 0.15),
                              color: '#9CA3AF',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                        ) : isExtractedOnly ? (
                          <Chip
                            label="📄 Extracted"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#3B82F6', 0.15),
                              color: '#3B82F6',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                        ) : isMatch ? (
                          <Chip
                            label="✓ Match"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#10B981', 0.15),
                              color: '#10B981',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                        ) : (
                          <Chip
                            label="✗ Mismatch"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#EF4444', 0.15),
                              color: '#EF4444',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isMatch ? (
                          <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>✓</Typography>
                        ) : isNoData ? (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        ) : result.discrepancy_type ? (
                          <Chip
                            label={result.discrepancy_type.replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              borderColor: alpha('#F59E0B', 0.6),
                              color: '#F59E0B',
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Classification Result */}
      {classification && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Classification
          </Typography>
          <Alert severity={classification.is_exception ? 'warning' : 'success'}>
            <Typography variant="subtitle1" gutterBottom>
              {classification.is_exception ? 'Exception Case' : 'Standard Case'}
            </Typography>
            {classification.exception_reasons.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" component="div">
                  <strong>Exception Reasons:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    {classification.exception_reasons.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                </Typography>
              </Box>
            )}
          </Alert>
        </Box>
      )}

      {/* ERP Data Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: erpVisible ? 'success.main' : 'divider',
          background: erpVisible
            ? `linear-gradient(135deg, ${alpha('#10B981', 0.04)} 0%, ${alpha('#059669', 0.04)} 100%)`
            : 'background.paper',
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: erpVisible ? 2 : 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ErpIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              ERP / MIS Reference Data
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              View {PROCESS_LABELS[processType].toLowerCase()} extraction data from cloud storage
            </Typography>
          </Box>
          <Button
            variant={erpVisible ? 'outlined' : 'contained'}
            size="small"
            onClick={handleViewErpData}
            disabled={loadingErp}
            startIcon={loadingErp ? <CircularProgress size={16} color="inherit" /> : <ViewIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              ...(erpVisible
                ? {
                  borderColor: '#10B981',
                  color: '#10B981',
                  '&:hover': { borderColor: '#059669', backgroundColor: alpha('#10B981', 0.08) },
                }
                : {
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                }),
            }}
          >
            {loadingErp ? 'Loading...' : erpVisible ? 'Hide ERP Data' : 'View ERP Data'}
          </Button>
        </Box>

        {erpError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {erpError}
          </Alert>
        )}

        {erpVisible && erpSections.length > 0 && (
          <Fade in>
            <Box
              sx={{
                maxHeight: 500,
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha('#10B981', 0.3),
                  borderRadius: 3,
                  '&:hover': { background: alpha('#10B981', 0.5) },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  label={`${erpSections.length} Section${erpSections.length > 1 ? 's' : ''}`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Source: {erpFilename}
                </Typography>
              </Box>

              {erpSections.map((section, sectionIndex) => (
                <Box
                  key={sectionIndex}
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                  }}
                >
                  {/* Section Header */}
                  <Box
                    onClick={() => toggleSection(sectionIndex)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      backgroundColor: alpha('#10B981', 0.08),
                      '&:hover': { backgroundColor: alpha('#10B981', 0.12) },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#10B981' }}>
                        {section.section}
                      </Typography>
                      <Chip
                        label={`${section.data.length} record${section.data.length > 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      {expandedSections.has(sectionIndex) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Section Table */}
                  {expandedSections.has(sectionIndex) && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {section.headers.map((header) => (
                              <TableCell key={header} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.data.map((row, rowIndex) => (
                            <TableRow
                              key={rowIndex}
                              sx={{
                                '&:nth-of-type(odd)': { backgroundColor: alpha('#10B981', 0.02) },
                                '&:hover': { backgroundColor: alpha('#10B981', 0.06) },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {section.headers.map((header) => (
                                <TableCell key={header} sx={{ fontSize: '0.8rem' }}>
                                  {header === section.headers[0] ? (
                                    <strong>{row[header] || '—'}</strong>
                                  ) : (
                                    row[header] || '—'
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ))}
            </Box>
          </Fade>
        )}

        {erpVisible && erpSections.length === 0 && !loadingErp && !erpError && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No ERP data available for {PROCESS_LABELS[processType]}.
          </Alert>
        )}
      </Paper>
    </>
  );
}
